/** @jsx jsx */
import { useTranslation } from 'react-i18next';
import { useField, FieldInputProps, useFormikContext, Field } from 'formik';
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Grid,
  Button,
  FormHelperText,
  Avatar,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

import {
  ICustomTheme,
  ChallengesType,
  IFormInputGroup,
  IFormInputChoice,
} from 'interfaces';
import { Title, Text } from 'components';
import { IPredefinedQuestions } from 'interfaces/challenge.types';
import Layout from '../StepLayout';
import { STEPS } from '../constants';

export const DEFAULT_INPUTS_MAX_WIDTH = 500;

const QuestionContainer = styled(Box)(({ theme }: { theme: ICustomTheme }) => ({
  marginBottom: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(3),
  },
}));

const QuestionAsk = styled(Title)(({ theme }: { theme: ICustomTheme }) => ({
  display: 'block',
  fontSize: 16,
  marginBottom: theme.spacing(2),
  textTransform: 'none',
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.body1.fontSize,
    marginBottom: theme.spacing(1),
  },
}));

const InputSelect = styled(Select)(
  ({ theme, width }: { theme: ICustomTheme; width: number }) => ({
    boxShadow: theme.shadows[1],
    maxWidth: '100%',
    padding: theme.spacing(1.5),
    width,
    '& .MuiSelect-icon': {
      color: theme.colors.grey.dark,
      fontSize: theme.typography.h3.fontSize,
      marginRight: theme.spacing(1),
    },
  }),
);

const InputBox = styled(Button)(
  ({
    theme,
    isSelected,
    isError,
  }: {
    theme: ICustomTheme;
    isSelected: boolean;
    isError: boolean;
  }) => ({
    background: `${theme.colors.paletteFromBO.main}${isSelected ? '99' : ''}`,
    border: `1px solid ${isError ? theme.colors.error.main : 'transparent'}`,
    borderRadius: theme.shape.borderRadius * 2,
    color: theme.colors.white,
    fontSize: 18,
    padding: theme.spacing(1, 4),
    textTransform: 'none',
    '&:hover': {
      background: `${theme.colors.paletteFromBO.main}99`,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.body2.fontSize,
    },
  }),
);

export const StyledTextField = styled(TextField)<{
  theme: ICustomTheme;
}>(({ theme }) => ({
  // input label when focused
  '& label.Mui-focused': {
    color: theme.colors.paletteFromBO.main,
  },
  // focused color for input with variant='standard'
  '& .MuiInput-underline:after': {
    borderBottomColor: theme.colors.paletteFromBO.main,
  },
  // focused color for input with variant='filled'
  '& .MuiFilledInput-underline:after': {
    borderBottomColor: theme.colors.paletteFromBO.main,
  },
  // focused color for input with variant='outlined'
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.colors.paletteFromBO.main,
    },
  },
}));

function Input({
  inputProps,
  setValue,
  setTouched,
  answers,
  isError,
}: {
  inputProps: FieldInputProps<unknown>;
  setValue: (value: string) => void;
  setTouched: (value: boolean) => void;
  answers: IFormInputChoice[];
  isError: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  const SELECT_DEFAULT_VALUE = 'none';

  if (!answers.length) {
    return (
      <StyledTextField
        {...inputProps}
        css={{ maxWidth: '100%' }}
        InputProps={{
          style: { maxWidth: '100%', width: DEFAULT_INPUTS_MAX_WIDTH },
        }}
        variant="outlined"
        error={isError}
      />
    );
  }

  if (answers.length < 5) {
    return (
      <Grid container spacing={3}>
        {answers.map((answer: IFormInputChoice) => (
          <Grid key={answer['@id']} item>
            <InputBox
              onClick={() => {
                setTouched(true);
                setValue(answer['@id']);
              }}
              variant="contained"
              isSelected={answer['@id'] === inputProps.value}
              isError={isError}
            >
              {answer.title}
            </InputBox>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <InputSelect
      {...inputProps}
      defaultValue={SELECT_DEFAULT_VALUE}
      error={isError}
      IconComponent={KeyboardArrowDownIcon}
      width={DEFAULT_INPUTS_MAX_WIDTH}
    >
      <MenuItem value={SELECT_DEFAULT_VALUE} disabled>
        <Text css={{ fontWeight: 600 }} isParagraph={false}>
          {t('challenges.declaration.questions.select')}
        </Text>
      </MenuItem>
      {answers.map((answer: IFormInputChoice) => (
        <MenuItem key={answer['@id']} value={answer['@id']}>
          {answer.title}
        </MenuItem>
      ))}
    </InputSelect>
  );
}

function Error({ error }: { error: string }): JSX.Element {
  return (
    <Grid container alignItems="center" spacing={1}>
      <Grid item>
        <Avatar
          css={(theme: ICustomTheme) => ({
            background: theme.colors.error.main,
          })}
        >
          <CloseIcon />
        </Avatar>
      </Grid>
      <Grid item>
        <FormHelperText>
          <Text
            fontWeight="bold"
            isParagraph={false}
            css={(theme: ICustomTheme) => ({
              [theme.breakpoints.down('sm')]: {
                fontSize: theme.typography.caption.fontSize,
              },
            })}
          >
            {error}
          </Text>
        </FormHelperText>
      </Grid>
    </Grid>
  );
}

function QuestionInput({
  answers,
  inputId,
}: {
  answers: IFormInputChoice[];
  inputId: number;
}): JSX.Element {
  const [field, meta, helpers] = useField(`${STEPS.answers}.${inputId}.answer`);
  const isError = meta.touched && Boolean(meta.error);

  return (
    <Grid container alignItems="center" spacing={3}>
      <Grid item css={{ maxWidth: '100%' }}>
        <Input
          inputProps={field}
          setValue={helpers.setValue}
          setTouched={helpers.setTouched}
          answers={answers}
          isError={isError}
        />
      </Grid>
      {isError && meta.error && (
        <Grid item>
          <Error error={meta.error} />
        </Grid>
      )}
    </Grid>
  );
}

function Question({
  ask,
  answers,
  index,
}: {
  ask: string;
  answers: IFormInputChoice[];
  index: number;
}): JSX.Element {
  return (
    <QuestionContainer>
      <QuestionAsk color="primary">{ask}</QuestionAsk>
      <QuestionInput inputId={index} answers={answers} />
    </QuestionContainer>
  );
}

const PredefinedSimpleQuestion = ({
  fieldName,
  label,
}: {
  fieldName: string;
  label: string;
  defaultValue?: string;
}): JSX.Element => {
  const [field, meta] = useField(fieldName);
  const isError = meta.touched && Boolean(meta.error);

  return (
    <div style={{ margin: '24px 0px' }}>
      <QuestionAsk color="primary">{label}</QuestionAsk>
      <StyledTextField
        {...field}
        css={{ maxWidth: '100%' }}
        InputProps={{
          style: { maxWidth: '100%', width: DEFAULT_INPUTS_MAX_WIDTH },
        }}
        variant="outlined"
        error={isError}
      />
      {isError && meta.error && (
        <Grid item>
          <Error error={meta.error} />
        </Grid>
      )}
    </div>
  );
};
const PredefinedDateQuestion = ({
  fieldName,
  label,
}: {
  fieldName: string;
  label: string;
  defaultValue?: string;
}): JSX.Element => {
  const [field, meta] = useField(`${fieldName}`);
  const { values } = useFormikContext<any>();
  const isError = meta.touched && Boolean(meta.error);
  const minDate = values.invoiceDateStart || undefined;

  return (
    <div style={{ maxWidth: 250, marginTop: 24 }}>
      <QuestionAsk color="primary">{label}</QuestionAsk>
      <Field
        as={TextField}
        name={`${fieldName}`}
        type="date"
        style={{ maxWidth: 450, margin: '8px 0px 24px 0px' }}
        fullWidth
        isError={isError}
        inputProps={{
          min: minDate,
        }}
      />
      {isError && meta.error && (
        <Grid item>
          <Error error={meta.error} />
        </Grid>
      )}
    </div>
  );
};

export const DeclarationFormQuestionsStep = ({
  declarationForm,
  predefinedQuestions,
}: ChallengesType): JSX.Element => {
  const { t } = useTranslation();

  const customQuestions = declarationForm?.formInputGroups;

  return (
    <Layout
      title={t('challenges.declaration.questions.title')}
      subtitle={t('challenges.declaration.questions.subtitle')}
      step={STEPS.answers}
    >
      {predefinedQuestions?.map((predefinedQuestion: IPredefinedQuestions) => {
        if (predefinedQuestion.isDisplayed) {
          if (
            predefinedQuestion.key === 'invoiceDateStart' ||
            predefinedQuestion.key === 'invoiceDateEnd'
          ) {
            return (
              <PredefinedDateQuestion
                fieldName={predefinedQuestion?.key}
                label={predefinedQuestion?.label}
              />
            );
          }
          return (
            <PredefinedSimpleQuestion
              fieldName={predefinedQuestion?.key}
              label={predefinedQuestion?.label}
            />
          );
        }
      })}
      {customQuestions?.map((question: IFormInputGroup, index: number) => (
        <Question
          key={question.formInputs[0]['@id']}
          index={index}
          ask={question.formInputs[0].label}
          answers={question.formInputs[0].formInputChoices}
        />
      ))}
    </Layout>
  );
};
